import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, ChevronDown, Filter, ClipboardCheck, CheckCircle2, XCircle, MessageSquare, AlertTriangle, Users } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import axios from 'axios';
import { TeamOverlapPreview } from '../../components/employee-portal/TeamOverlapPreview';
import { GroupDatesSelector } from '../../components/leaves/GroupDatesSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const typeColors = {
  annual: 'bg-blue-100 text-blue-700',
  sick: 'bg-red-100 text-red-700',
  emergency: 'bg-orange-100 text-orange-700',
  unpaid: 'bg-gray-200 text-gray-700',
  mgmt_unpaid: 'bg-amber-100 text-amber-800 border border-amber-300',
  maternity: 'bg-pink-100 text-pink-700',
  paternity: 'bg-purple-100 text-purple-700',
  public_holiday: 'bg-green-100 text-green-700',
};

const statusColors = {
  approved: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
};

const PortalLeaves = () => {
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [typeFilter, setTypeFilter] = useState('all');
  const location = useLocation();
  const initTab = (() => {
    const t = new URLSearchParams(location.search).get('tab');
    return t === 'approvals' ? 'approvals' : 'my-leaves';
  })();
  const [tab, setTab] = useState(initTab); // my-leaves | approvals
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [rejectDates, setRejectDates] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [actionComment, setActionComment] = useState('');
  const [actionLeaveId, setActionLeaveId] = useState(null);
  const [acting, setActing] = useState(false);
  const [joinYear, setJoinYear] = useState(null);
  const [clashConfirm, setClashConfirm] = useState(null); // { leaveId, clash }
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch join date on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/me`, { headers });
        const jd = res.data?.join_date;
        if (jd) setJoinYear(new Date(jd + 'T00:00:00').getFullYear());
      } catch (err) { console.error(err); }
    };
    fetchProfile();
  }, [token]);

  useEffect(() => {
    const load = async () => {
      try {
        const [balRes, leavesRes] = await Promise.all([
          axios.get(`${API}/api/employee-portal/my-balance?year=${yearFilter}`, { headers }),
          axios.get(`${API}/api/employee-portal/my-leaves?year=${yearFilter}`, { headers })
        ]);
        setBalance(balRes.data);
        setLeaves(leavesRes.data);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('portal_token');
          navigate('/employee-portal/login');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate, yearFilter]);

  const loadApprovals = async () => {
    setApprovalsLoading(true);
    try {
      const res = await axios.get(`${API}/api/employee-portal/pending-approvals`, { headers });
      const list = res.data || [];
      setPendingApprovals(list);
      // Enrich each pending leave with a department clash check (advisory)
      const enriched = await Promise.all(list.map(async (leave) => {
        if (!leave.start_date || !leave.end_date || !leave.employee_id) return leave;
        try {
          const chk = await axios.get(`${API}/api/employee-portal/leave-overlap-check`, {
            params: { employee_id: leave.employee_id, start_date: leave.start_date, end_date: leave.end_date },
            headers,
          });
          return { ...leave, clash: chk.data };
        } catch {
          return leave;
        }
      }));
      setPendingApprovals(enriched);
    } catch { setPendingApprovals([]); }
    setApprovalsLoading(false);
  };

  useEffect(() => {
    if (tab === 'approvals') loadApprovals();
  }, [tab]);

  useEffect(() => {
    if (typeFilter === 'all') {
      setFilteredLeaves(leaves);
    } else {
      setFilteredLeaves(leaves.filter(l => l.leave_type === typeFilter));
    }
  }, [leaves, typeFilter]);

  const handleLeaveAction = async (leaveId, action, skipClashCheck = false) => {
    // Require manager confirmation when approving would breach the department clash threshold
    if (action === 'approve' && !skipClashCheck) {
      const leave = pendingApprovals.find(l => l.id === leaveId);
      const clash = leave?.clash;
      if (clash && clash.warn) {
        setClashConfirm({ leaveId, clash });
        return;
      }
    }
    setActing(true);
    try {
      const lv = pendingApprovals.find(l => l.id === leaveId);
      if (lv?.group_id) {
        await axios.patch(`${API}/api/employee-portal/leave-group/${lv.group_id}/decide`, {
          action, comment: actionComment, reject_dates: action === 'approve' ? rejectDates : [],
        }, { headers });
      } else {
        await axios.patch(`${API}/api/employee-portal/leave-action/${leaveId}`, {
          action,
          comment: actionComment,
        }, { headers });
      }
      setRejectDates([]);
      setActionLeaveId(null);
      setActionComment('');
      setClashConfirm(null);
      loadApprovals();
    } catch (err) { console.error(err); }
    setActing(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="portal-leaves">
      <h2 className="text-lg font-bold text-gray-900">Leaves</h2>

      {/* Tab Switcher */}
      <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-xl">
        <button
          onClick={() => setTab('my-leaves')}
          className={`py-2 rounded-lg text-xs font-medium transition ${
            tab === 'my-leaves' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
          }`}
          data-testid="tab-my-leaves"
        >
          My Leaves
        </button>
        <button
          onClick={() => setTab('approvals')}
          className={`py-2 rounded-lg text-xs font-medium transition flex items-center justify-center gap-1 ${
            tab === 'approvals' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
          }`}
          data-testid="tab-approvals"
        >
          <ClipboardCheck className="h-3.5 w-3.5" />
          Approvals
          {pendingApprovals.length > 0 && (
            <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{pendingApprovals.length}</span>
          )}
        </button>
      </div>

      {tab === 'my-leaves' ? (
        <>
          {/* Balance Summary */}
          {balance && (
            <Card className="border-0 shadow-sm bg-gradient-to-r from-indigo-50 to-purple-50" data-testid="leave-summary-card">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-[10px] text-gray-500">Total Available</p>
                    <p className="text-xl font-bold text-indigo-600">{balance.annual_total?.toFixed(1)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-[10px] text-gray-500">Balance</p>
                    <p className="text-xl font-bold text-green-600">{balance.annual_balance?.toFixed(1)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-[10px] text-gray-500">Annual Taken</p>
                    <p className="text-xl font-bold text-gray-700">{balance.annual_taken?.toFixed(1)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded-lg">
                    <p className="text-[10px] text-gray-500">Total All Types</p>
                    <p className="text-xl font-bold text-gray-700">{balance.total_taken?.toFixed(1)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <div className="flex gap-2">
            <Select value={yearFilter} onValueChange={(v) => { setYearFilter(v); setLoading(true); }}>
              <SelectTrigger className="flex-1" data-testid="portal-year-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(() => {
                  const currentYear = new Date().getFullYear();
                  const startYear = joinYear || currentYear - 2;
                  const years = [];
                  for (let y = currentYear; y >= startYear; y--) {
                    years.push(y);
                  }
                  return years.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="flex-1" data-testid="portal-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
                <SelectItem value="sick">Sick</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="mgmt_unpaid">MGMT Unpaid</SelectItem>
                <SelectItem value="maternity">Maternity</SelectItem>
                <SelectItem value="paternity">Paternity</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Leave List */}
          <div className="space-y-2">
            {filteredLeaves.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6 text-center text-sm text-gray-400">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  No data available for {yearFilter}. Please contact HR.
                </CardContent>
              </Card>
            ) : (
              filteredLeaves.map(leave => (
                <Card key={leave.id} className="border-0 shadow-sm" data-testid={`leave-row-${leave.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${typeColors[leave.leave_type] || 'bg-gray-100'}`}>
                          {leave.leave_type === 'mgmt_unpaid' ? 'MGMT unpaid' : leave.leave_type?.replace('_', ' ')}
                        </span>
                        <Badge className={`text-[9px] ${statusColors[leave.status] || ''}`}>{leave.status}</Badge>
                      </div>
                      <span className="text-sm font-bold text-gray-700">{leave.days}d</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {leave.start_date} &rarr; {leave.end_date}
                    </p>
                    {leave.reason && (
                      <p className="text-[10px] text-gray-400 mt-1 truncate">{leave.reason}</p>
                    )}
                    {leave.actioned_by && (
                      <p className="text-[10px] text-gray-400 mt-1">
                        {leave.status === 'approved' ? 'Approved' : 'Rejected'} by {leave.actioned_by}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      ) : (
        /* Approvals Tab */
        <div className="space-y-2" data-testid="approvals-tab">
          {approvalsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" />
            </div>
          ) : pendingApprovals.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 text-center text-sm text-gray-400">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                No pending approvals
              </CardContent>
            </Card>
          ) : (
            pendingApprovals.map(leave => (
              <Card key={leave.id} className="border-0 shadow-sm" data-testid={`approval-${leave.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{leave.employee_name}</p>
                      <p className="text-[10px] text-gray-400">{leave.employee_code} - {leave.department}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${typeColors[leave.leave_type] || 'bg-gray-100'}`}>
                      {leave.leave_type?.replace('_', ' ')}
                    </span>
                  </div>
                  {leave.group_dates ? (
                    <div className="text-xs text-gray-600" data-testid={`approval-dates-${leave.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-sky-700 font-medium">Separate dates request</span>
                        <span className="font-bold">{leave.days - (actionLeaveId === leave.id ? rejectDates.length : 0)} days</span>
                      </div>
                      <GroupDatesSelector dates={leave.group_dates.filter(g => g.status === 'pending').map(g => g.date)}
                        rejected={actionLeaveId === leave.id ? rejectDates : []} onChange={setRejectDates}
                        editable={actionLeaveId === leave.id} testPrefix={`approval-date-${leave.id}`} />
                      {actionLeaveId === leave.id && <p className="text-[10px] text-gray-400 mt-1">Tap a range to reject it whole, or a single day; Approve applies to the rest.</p>}
                    </div>
                  ) : (
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>{leave.start_date} &rarr; {leave.end_date}</span>
                    <span className="font-bold">{leave.days} days</span>
                  </div>
                  )}
                  {leave.annual_balance && (
                    <div className="mt-1.5 text-[11px] flex items-center gap-1" data-testid={`approval-balance-${leave.id}`}>
                      <span className="text-gray-400">Annual:</span>
                      {leave.annual_balance.overdrawn ? (
                        <span className="text-gray-600">
                          {leave.annual_balance.total} of {leave.annual_balance.total} used ·{' '}
                          <span className="text-rose-600 font-semibold">over-drawn by {Math.abs(leave.annual_balance.remaining)}d</span>
                        </span>
                      ) : (
                        <span className="text-gray-600">
                          {leave.annual_balance.used} of {leave.annual_balance.total} used ·{' '}
                          <span className="text-emerald-600 font-semibold">{leave.annual_balance.remaining} remaining</span>
                        </span>
                      )}
                      <span className="text-gray-300">(before this request)</span>
                    </div>
                  )}
                  {leave.type_usage && (
                    <div className="mt-1 text-[11px] flex items-center gap-1 flex-wrap" data-testid={`approval-type-usage-${leave.id}`}>
                      <span className="text-gray-400 capitalize">{(leave.leave_type || '').replace(/_/g, ' ')} this year:</span>
                      <span className="text-gray-600">
                        <span className="font-semibold">{leave.type_usage.taken}d taken</span> in {leave.type_usage.year}
                      </span>
                      <span className="text-gray-300">({(Math.round((leave.type_usage.taken + leave.days) * 10) / 10)}d incl. this)</span>
                    </div>
                  )}
                  {leave.reason && (
                    <p className="text-[10px] text-gray-500 mt-1.5 bg-gray-50 rounded p-2">{leave.reason}</p>
                  )}

                  <TeamOverlapPreview overlap={leave.team_overlap} leaveId={leave.id} />
                  {leave.clash && leave.clash.count > 0 && (
                    <div
                      className={`mt-2 rounded-lg p-2.5 text-[11px] border ${leave.clash.warn ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                      data-testid={`approval-clash-${leave.id}`}
                    >
                      <div className="flex items-center gap-1.5 font-semibold">
                        {leave.clash.warn
                          ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                          : <Users className="h-3.5 w-3.5 shrink-0" />}
                        {leave.clash.warn
                          ? `Understaffing risk — up to ${leave.clash.peak_concurrent} already off at once in ${leave.clash.department} (limit ${leave.clash.threshold})`
                          : `${leave.clash.count} teammate(s) off in ${leave.clash.department} during this window`}
                      </div>
                      <ul className="mt-1 space-y-0.5 pl-5 list-disc">
                        {leave.clash.overlaps.slice(0, 4).map((o, i) => (
                          <li key={i}>
                            {o.employee_name} · {(o.leave_type || '').replace(/_/g, ' ')} · {o.start_date} → {o.end_date}
                          </li>
                        ))}
                        {leave.clash.overlaps.length > 4 && (
                          <li className="list-none text-[10px] opacity-70">…and {leave.clash.overlaps.length - 4} more</li>
                        )}
                      </ul>
                    </div>
                  )}

                  {actionLeaveId === leave.id ? (
                    <div className="mt-3 space-y-2">
                      <Input
                        value={actionComment}
                        onChange={e => setActionComment(e.target.value)}
                        placeholder="Add a comment (optional)"
                        className="h-8 text-xs"
                        data-testid={`comment-${leave.id}`}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm" className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700 gap-1"
                          onClick={() => handleLeaveAction(leave.id, 'approve')}
                          disabled={acting}
                          data-testid={`confirm-approve-${leave.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> {leave.group_id && rejectDates.length ? `Approve ${leave.group_dates.filter(g => g.status === 'pending').length - rejectDates.length}, reject ${rejectDates.length}` : 'Approve'}
                        </Button>
                        <Button
                          size="sm" variant="destructive" className="flex-1 h-8 text-xs gap-1"
                          onClick={() => handleLeaveAction(leave.id, 'reject')}
                          disabled={acting}
                          data-testid={`confirm-reject-${leave.id}`}
                        >
                          <XCircle className="h-3.5 w-3.5" /> Reject
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setActionLeaveId(null); setActionComment(''); }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm" className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700 gap-1"
                        onClick={() => { setRejectDates([]); setActionLeaveId(leave.id); }}
                        data-testid={`approve-btn-${leave.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                      </Button>
                      <Button
                        size="sm" variant="destructive" className="flex-1 h-8 text-xs gap-1"
                        onClick={() => { setRejectDates([]); setActionLeaveId(leave.id); }}
                        data-testid={`reject-btn-${leave.id}`}
                      >
                        <XCircle className="h-3.5 w-3.5" /> Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      <AlertDialog open={!!clashConfirm} onOpenChange={(o) => { if (!o) setClashConfirm(null); }}>
        <AlertDialogContent data-testid="clash-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" /> Department understaffing risk
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-gray-600 space-y-2">
                {clashConfirm && (
                  <>
                    <p>
                      Up to <span className="font-bold text-rose-600">{clashConfirm.clash.peak_concurrent}</span> teammate(s)
                      in <span className="font-semibold">{clashConfirm.clash.department}</span> are already off at once
                      {clashConfirm.clash.peak_date ? ` on ${clashConfirm.clash.peak_date}` : ''} — the configured limit is{' '}
                      <span className="font-semibold">{clashConfirm.clash.threshold}</span>.
                    </p>
                    <p>Approving this leave will add one more absence during that period. Approve anyway?</p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clash-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => clashConfirm && handleLeaveAction(clashConfirm.leaveId, 'approve', true)}
              data-testid="clash-approve-anyway-btn"
            >
              Approve anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PortalLeaves;
